import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, catchError, map, of, BehaviorSubject, interval, Subscription } from 'rxjs';
import { _baseUrl, _sebo_subdomain } from '../../helper/global-helper';

@Injectable({
    providedIn: 'root',
})
export class DidDataRetrievalService {
    private baseUrl: string;
    private seboImgUrl: string;
    private seboFlagUrl: string;
    private seboReportUrl: string;

    public queryLive: boolean = false;
    private queryLiveSubject = new BehaviorSubject<boolean>(false);
    public queryLiveObs = this.queryLiveSubject.asObservable();
    private sendQueryAliveFrequency: number = 1000;

    public queryFrequency: number = 0;

    public startTime: number = 0;
    private timer: Subscription = new Subscription;

    constructor(private http: HttpClient) {
        this.baseUrl = _baseUrl;
        this.seboImgUrl = _sebo_subdomain + '/api/v1/testImage?id=';
        this.seboFlagUrl = _sebo_subdomain + '/api/v1/enableEol';
        this.seboReportUrl = _sebo_subdomain + '/api/v1/reportEol?machine=';
    }

    public initService(){
        this.startSendQueryAliveState();
    }

    public startSendQueryAliveState(): void {
        this.timer = interval(this.sendQueryAliveFrequency).subscribe(() => {
            this.sendQueryAliveState();
        });
    }

    public stopSendQueryAliveState(): void {
        if(this.timer != undefined) {
            this.queryLive = false;
            this.timer.unsubscribe();  
        }
    }

    private sendQueryAliveState(): void {
        this.checkDataQueryLive();
        this.queryLiveSubject.next(this.queryLive);
    }

    private checkDataQueryLive(): boolean {
        this.http
            .get<{ queryLive: boolean }>(this.baseUrl + 'dataquery', {
                observe: 'body',
                responseType: 'json',
            })
            .pipe(
                map((body) => body.queryLive),
                catchError(() => of(false))
            )
            .subscribe({
                next: (queryLiveResponse) => {
                    this.queryLive = queryLiveResponse;
                },
            });
        return this.queryLive;
    }

    public startDataQuery(frequency?: number): number {
        let resp: any;
        this.http.post(this.baseUrl + 'dataquery?msfrequency=' + frequency , null, {
                observe: 'response',
                responseType: 'json',
            })
            .subscribe({
                next: (response) => {
                    resp = response.body;
                    this.queryFrequency = resp.frequency;
                    this.sendQueryAliveState();
                },
            });
        return this.queryFrequency;
    }

    public stopDataQuery(): void {
        this.http
            .delete<HttpResponse<VoidFunction>>(this.baseUrl + 'dataquery', {
                observe: 'response',
                responseType: 'json',
            })
            .subscribe({
                next: () => {
                    this.sendQueryAliveState();
                },
            });
    }

    public getAllDidsValues(): Observable<HttpResponse<t_DataPoint[]>> {
        let response = this.http
            .get<t_DataPoint[]>(this.baseUrl + 'dataresource/values', {
                observe: 'response',
                responseType: 'json',
            })
            .pipe(
                map((response) => {
                    (response.body as t_DataPoint[]).forEach((dataPoint) => {
                        if (dataPoint.data?.timestamp) {
                            dataPoint.data!.value = Object.values(dataPoint.data!.value);
                            let valueBuffer: number = this.combine8BitNumbersTo32Bit(dataPoint.data!.value);
                            dataPoint.data!.value = [valueBuffer];
                        }
                    });
                    return response;
                })
            );
        return response;
    }

    public getDidsDetails(dataIdentifierList: t_DataIdentifier[]): Observable<HttpResponse<t_DataPointTimeSeries[]>> {
        dataIdentifierList = this.numerizeDataIdentifierList(dataIdentifierList);
        let requestBody: JSON = JSON.parse(JSON.stringify(dataIdentifierList));

        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        let response = this.http
            .put<t_DataPointTimeSeries[]>(this.baseUrl + 'dataresource/values/dids', requestBody, {
                observe: 'response',
                responseType: 'json',
                headers: headers,
            })
            .pipe(
                map((response) => {
                    (response.body as t_DataPointTimeSeries[]).forEach((didDetails) => {
                        didDetails.data?.forEach((value) => {
                            if(value.value != undefined){
                                value.value = Object.values(value.value);
                                let valueBuffer: number = this.combine8BitNumbersTo32Bit(value.value);
                                value.value = [valueBuffer];
                            }
                            
                        });
                    });
                    return response;
                })
            );
        return response;
    }

    public getRawDidsDetails(dataIdentifierList: t_DataIdentifier[]): Observable<HttpResponse<t_DataPointTimeSeries[]>> {
        dataIdentifierList = this.numerizeDataIdentifierList(dataIdentifierList);
        let requestBody: JSON = JSON.parse(JSON.stringify(dataIdentifierList));

        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        let response = this.http
            .put<t_DataPointTimeSeries[]>(this.baseUrl + 'dataresource/values/dids', requestBody, {
                observe: 'response',
                responseType: 'json',
                headers: headers,
            });
        return response;
    }

    public getComissionStatusDidsDetails(dataIdentifierList: t_DataIdentifier[]): Observable<HttpResponse<t_DataPointTimeSeries[]>> {
        dataIdentifierList = this.numerizeDataIdentifierList(dataIdentifierList);
        let requestBody: JSON = JSON.parse(JSON.stringify(dataIdentifierList));

        let headers = new HttpHeaders().set('Content-Type', 'application/json');

        let response = this.http
            .put<t_DataPointTimeSeries[]>(this.baseUrl + 'dataresource/values/dids', requestBody, {
                observe: 'response',
                responseType: 'json',
                headers: headers,
            })
            .pipe(
                map((response) => {
                    (response.body as t_DataPointTimeSeries[]).forEach((didDetails) => {
                        didDetails.data?.forEach((value) => {
                            if(value.value != undefined){
                                value.value = Object.values(value.value);
                                let valueBuffer: number[] = this.combine8BitNumbersTo16Bit(value.value);
                                value.value = valueBuffer;
                            }
                            
                        });
                    });
                    return response;
                })
            );
        return response;
    }

    private combine8BitNumbersTo32Bit(values: number[]): number {
        let result: number = 0;
        for (let i = 0; i < values.length; i++) {
            const byteValue = values[i] & 0xff;
            result |= byteValue << (8 * (values.length - 1 - i));
        }
        return result;
    }

    // 2 bytes int 16 - progress, 2 bytes int 16 - error
    private combine8BitNumbersTo16Bit(values: number[]): number[] {
        const progress = ((values[0] & 0xFF) << 8) | (values[1] & 0xFF);
        const error = ((values[2] & 0xFF) << 8) | (values[3] & 0xFF);
        const signedProgress = progress >= 0x8000 ? progress - 0x10000 : progress;
        //console.log(values, progress, signedProgress, error);
        
        return [signedProgress, error];
    }

    private numerizeDataIdentifierList(dataIdentifierList: t_DataIdentifier[]) {
        dataIdentifierList.forEach((dataIdentifier) => {
            dataIdentifier.ecu_id = Number(dataIdentifier.ecu_id);
            dataIdentifier.data_id = Number(dataIdentifier.data_id);
        });

        return dataIdentifierList;
    }

    public cleanDataPoints(): number {
        let resCode: number = 404;
        this.http
            .delete<HttpResponse<VoidFunction>>(this.baseUrl + 'dataresource/values/', {
                observe: 'response',
                responseType: 'json',
            })
            .subscribe({
                next: (res) => {
                    resCode = res.status;
                },
            });
        return resCode;
    }

    public setStartTime(time: number){
        this.startTime = time;
    }

    public getStartTime() {
        return this.startTime;
    }

    public getTestImg(imgName: string) {
        return this.http.get(this.seboImgUrl + imgName, {
            observe: 'response',
            responseType: 'blob',
        });
    }

    public getReport(number: string) {
        return this.http.get(this.seboReportUrl + number, {
            observe: 'response',
            responseType: 'blob',
        });
    }

    public setEOLTestDoneFlag(machine: string) {
        let body = {"machine": machine, "eolTestDone": true};
        return this.http.post(this.seboFlagUrl, body, {
            observe: 'response',
            responseType: 'json',
        });
    }

}

export interface DidDetails {
    did: number;
    values: TimeSeries[];
}

export interface TimeSeries {
    timestamp: Date;
    value: number[];
}

export interface t_DataValue {
    timestamp: Date;
    value: number[];
}

export type t_DataIdentifier = {
    ecu_id: number;
    data_id: number;
};

export type t_DataPointTimeSeries = {
    identifier: t_DataIdentifier;
    data?: t_DataValue[];
};

export type t_DataPoint = {
    identifier: t_DataIdentifier;
    data?: t_DataValue;
};
